// Scene lights — the ONE definition of the hemisphere + sun that light every
// runtime-lit object (avatars, weapons, bike, lifts, rails). Pure (no three
// import) so scripts/check-building-light.mjs can load it in node.
//
// Buildings do NOT use these at runtime: pipeline/buildings.py
// bake_vertex_light folds the same lights into COLOR_0 and the client draws
// them MeshBasic. So the runtime lights must be the ones the bake used, or a
// bike parked by a wall is lit from another side than the wall:
//   * colours + base intensities: the constants below == buildings.py
//     HEMI_* / SUN_* (asserted by check-building-light);
//   * direction + exposure (lighting_v2) and the sky/sun intensities
//     (lighting_v3): read from the bake (sceneLight) — they ship in
//     meta.buildings.vertex_light (the world's sun, region.lighting, the one
//     the terrain is shaded with).
// lighting_v4: the bake runs NO curve (COLOR_0 is the plain irradiance), so
// runtime-lit objects agree with the bake exactly; the renderer's one display
// curve (renderconfig.json `look`, look.js setupLook) rolls highlights off for
// both. Terrain, panorama, trees, objects are pre-lit and never see these
// lights (terrain + panorama on the same light model: bake.py _light_model).

// lighting_v3b: sky 0.41 / sun 1.07 (v2: 1.05 / 1.35; v3 0.36 / 1.0 was
// "too dark") — see buildings.py HEMI_INTENSITY for why the sky's share came down.
export const HEMI = { sky: 0xcfe4ff, ground: 0x6d675e, intensity: 0.41 };
export const SUN = { color: 0xfff2dd, intensity: 1.07 };
// == buildings.py LIGHT_EXPOSURE: three's physical lights (BRDF / π) at the
// base intensities left lit objects ~⅓ darker than the full-albedo terrain.
export const EXPOSURE = Math.PI;

const unit = (v) => {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
};

// region.lighting → unit vector toward the sun in game axes (+x east, +y up,
// +z south); azimuth clockwise from north — bake.py bake_light, sky.js
// buildHorizon and buildings.py sun_dir use the same convention.
export function sunDirFromLighting(lt) {
  if (!lt || lt.sun_az_deg == null || lt.sun_alt_deg == null) return null;
  const az = (lt.sun_az_deg * Math.PI) / 180, alt = (lt.sun_alt_deg * Math.PI) / 180;
  return unit([Math.sin(az) * Math.cos(alt), Math.sin(alt), -Math.cos(az) * Math.cos(alt)]);
}

// {dir, exposure, hemi, sun, src} for this bake — main.js builds
// HemisphereLight(HEMI colours, hemi * exposure) and DirectionalLight(SUN
// colour, sun * exposure) along dir. What the buildings were BAKED with wins
// (exact agreement with them: the spec carries its own exposure and
// intensities); a bake with no pre-lit buildings follows the world's sun
// (meta.lighting, which every bake carries — missing, or a vertex_light
// spec without its exposure / intensities, is a broken bake and throws).
export function sceneLight(meta) {
  const vl = meta && meta.buildings && meta.buildings.vertex_light;
  if (vl) {
    for (const k of ['exposure', 'hemi_intensity', 'sun_intensity']) {
      if (!Number.isFinite(vl[k])) throw new Error(`world meta.buildings.vertex_light.${k} missing: rebuild this world (Build)`);
    }
    if (!Array.isArray(vl.sun_dir) || vl.sun_dir.length !== 3) {
      throw new Error('world meta.buildings.vertex_light.sun_dir missing: rebuild this world (Build)');
    }
    return { dir: unit(vl.sun_dir), exposure: vl.exposure, hemi: vl.hemi_intensity, sun: vl.sun_intensity, src: 'bake' };
  }
  const d = sunDirFromLighting(meta && meta.lighting);
  if (!d) throw new Error('world meta.lighting missing (sun_az_deg / sun_alt_deg): rebuild this world (Build)');
  return { dir: d, exposure: EXPOSURE, hemi: HEMI.intensity, sun: SUN.intensity, src: 'lighting' };
}
